import {handleSubmit} from 'app/client/lib/formUtils';
import {handleFormError} from 'app/client/lib/loginFormUtils';
import {urlState} from 'app/client/models/gristUrlState';
import {cssLink} from 'app/client/ui2018/links';
import {textInput} from 'app/client/ui/inputs';
import {getLoginStateFromUrl} from 'app/client/ui/LoginPage';
import * as css from 'app/client/ui/LoginPagesCss';
import {ApiError} from 'app/common/ApiError';
import {ChallengeRequired, LoginAPI} from 'app/common/LoginAPI';
import {dom, IDisposableOwner, makeTestId, Observable, styled} from 'grainjs';

const testId = makeTestId('test-lp-');

interface LoginFormOptions {
  email: Observable<string>;
  password: Observable<string>;
  loginAPI: LoginAPI;
  onChallengeRequired: (challenge: ChallengeRequired) => void;
  onEmailVerificationRequired: () => void;
}

/**
 * Builds a login form for an existing Grist account. Supports logging in with
 * Google, or email and password.
 *
 * On successful submission of the email/password form, if a user does not have
 * any 2FA methods enabled for their account, they will be redirected to the
 * main Grist application. Otherwise, `onChallengeRequired` will be called, which
 * should advance the flow to the second factor verification step.
 *
 * If the user associated with the email is unverified, `onEmailVerificationRequired`
 * will be called, which changes the flow to email verification.
 *
 * Google Sign-In redirects to Google's OAuth flow, which further redirects to the
 * main Grist application on successful authentication with Google.
 */
export function buildLoginForm(owner: IDisposableOwner, options: LoginFormOptions) {
  const {
    email: emailObs,
    password: passwordObs,
    loginAPI,
    onChallengeRequired,
    onEmailVerificationRequired
  } = options;

  const error = Observable.create(owner, '');
  const pending = Observable.create(owner, false);

  return dom.frag(
    css.gristLogo(),
    cssHeading('Welcome back', testId('heading')),
    cssMessage('Please sign in to continue to your Grist account.'),
    dom('form',
      css.googleButton(css.googleLogo(), 'Sign in with Google'),
      handleSubmit(pending, async () => {
        window.location.assign(loginAPI.getGoogleSignInUrl(getLoginStateFromUrl()));
      }),
      testId('google-sign-in'),
    ),
    css.loginMethodsSeparator(css.horizontalLine(), css.lightText('or'), css.horizontalLine()),
    dom('form',
      dom.maybe(error, (err) => css.centeredFormError(err, testId('form-error'))),
      cssLabelAndInput(
        css.formLabel('Email', {for: 'email'}),
        textInput(emailObs,
          {
            id: 'email',
            name: 'email',
            autocomplete: 'username',
            placeholder: 'name@host.com',
            type: 'email',
            required: 'true',
          },
          (el) => { setTimeout(() => el.focus(), 10); },
          testId('email-input'),
        ),
      ),
      cssPassword(
        css.formLabel('Password', {for: 'current-password'}),
        textInput(passwordObs,
          {
            id: 'current-password',
            name: 'password',
            autocomplete: 'current-password',
            placeholder: 'Enter password',
            type: 'password',
            required: 'true',
          },
          testId('password-input'),
        ),
      ),
      cssForgotPassword(cssLink('Forgot your password?',
        urlState().setLinkUrl({login: 'forgot-password'}),
        testId('forgot-password'),
      )),
      cssSignInButton('Sign In',
        dom.boolAttr('disabled', pending),
        testId('sign-in')
      ),
      css.centeredText("Don't have an account? ",
        cssLink('Sign up',
          urlState().setLinkUrl({login: 'signup'}),
          testId('sign-up'),
        ),
      ),
      handleSubmit(pending,
        ({email, password}) => loginAPI.logIn(email, password, {state: getLoginStateFromUrl()}),
        (result) => {
          if (result.isChallengeRequired === false) {
            window.location.assign(result.redirectUrl);
          } else {
            onChallengeRequired(result);
          }
        },
        (e) => {
          if (e instanceof ApiError && e.details?.code === 'UserNotConfirmed') {
            onEmailVerificationRequired();
          } else {
            handleFormError(e, error);
          }
        },
      ),
    ),
  );
}

const cssHeading = styled(css.formHeading, `
  margin-top: 16px;
  text-align: center;
`);

const cssMessage = styled(css.centeredText, `
  margin-bottom: 28px;
`);

const cssLabelAndInput = styled('div', `
  margin-bottom: 20px;
`);

const cssSignInButton = styled(css.bigPrimaryButton, `
  width: 100%;
  margin-bottom: 24px;
`);

const cssPassword = styled(cssLabelAndInput, `
  margin-bottom: 16px;
`);

const cssForgotPassword = styled('div', `
  margin-bottom: 32px;
`);
